import { useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const API_BASE = import.meta.env.VITE_API_URL || `http://${window.location.hostname}:7070`;

const styles = {
  page: {
    minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: '#0F172A', fontFamily: 'system-ui, sans-serif',
  },
  card: {
    background: '#1E293B', borderRadius: 12, padding: '2rem', width: '100%', maxWidth: 380,
    boxShadow: '0 4px 24px rgba(0,0,0,0.25)', border: '1px solid #334155',
  },
  title: { margin: '0 0 0.5rem', fontSize: '1.4rem', fontWeight: 700, color: '#F8FAFC' },
  subtitle: { margin: '0 0 1.5rem', fontSize: '0.9rem', color: '#94A3B8' },
  label: { display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#94A3B8', marginBottom: 4 },
  input: {
    width: '100%', padding: '0.6rem 0.75rem', borderRadius: 8, border: '1.5px solid #334155',
    fontSize: '1rem', boxSizing: 'border-box', outline: 'none', marginBottom: '1rem',
    background: '#1E293B', color: '#F8FAFC',
  },
  btn: {
    width: '100%', padding: '0.75rem', borderRadius: 8, border: 'none',
    background: 'linear-gradient(135deg, #7C3AED, #06B6D4)', color: '#fff', fontSize: '1rem', fontWeight: 600,
    cursor: 'pointer', marginTop: 4,
  },
  error: { color: '#EF4444', fontSize: '0.875rem', marginBottom: '1rem' },
};

export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  if (!token) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <h1 style={styles.title}>{t('resetPassword.invalidTitle')}</h1>
          <p style={styles.subtitle}>{t('resetPassword.invalidSubtitle')}</p>
          <Link to="/admin/login" style={{ color: '#7C3AED', fontWeight: 600 }}>
            {t('resetPassword.backToLogin')}
          </Link>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) { setError(t('resetPassword.minLength')); return; }
    if (newPassword !== confirm) { setError(t('resetPassword.mismatch')); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/v1/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword }),
      });
      if (!res.ok) {
        const data = await res.json();
        setError(data.message || t('resetPassword.error'));
        return;
      }
      setSuccess(true);
      setTimeout(() => navigate('/admin/login'), 3000);
    } catch {
      setError(t('resetPassword.networkError'));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <h1 style={styles.title}>{t('resetPassword.successTitle')}</h1>
          <p style={styles.subtitle}>{t('resetPassword.successSubtitle')}</p>
          <Link to="/admin/login" style={{ color: '#7C3AED', fontWeight: 600 }}>
            {t('resetPassword.backToLogin')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <h1 style={styles.title}>{t('resetPassword.title')}</h1>
        <p style={styles.subtitle}>{t('resetPassword.subtitle')}</p>
        {error && <p style={styles.error}>{error}</p>}
        <form onSubmit={handleSubmit}>
          <label style={styles.label}>{t('resetPassword.passwordLabel')}</label>
          <div style={{ position: 'relative', marginBottom: '1rem' }}>
            <input
              style={{ ...styles.input, marginBottom: 0, paddingRight: '3rem' }}
              type={showPw ? 'text' : 'password'}
              placeholder={t('resetPassword.passwordPlaceholder')}
              value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
              required minLength={8}
            />
            <button type="button" onClick={() => setShowPw(v => !v)} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.8rem', color: '#94A3B8', padding: 4 }}>
              {showPw ? t('resetPassword.hide') : t('resetPassword.show')}
            </button>
          </div>
          <label style={styles.label}>{t('resetPassword.confirmLabel')}</label>
          <input
            style={styles.input} type="password"
            value={confirm} onChange={(e) => setConfirm(e.target.value)} required
          />
          <button style={styles.btn} type="submit" disabled={loading}>
            {loading ? t('resetPassword.submitting') : t('resetPassword.submit')}
          </button>
        </form>
      </div>
    </div>
  );
}
